import { useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { ja } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import "./DocumentEdit.css";
import "../DocumentRegister/DocumentRegister.css";

registerLocale("ja", ja);

const PatternSystemEdit = ({
  formData,
  onFormDataChange,
  documentCategories = [],
  documentCategoryText,
  issueDateText,
}) => {
  // 文字の幅カウント
  const getCharacterWidth = (text) => {
    if (!text) return 0;
    let width = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const code = char.charCodeAt(0);
      // Check if character is full-width (zenkaku)
      if (
        (code >= 0x3040 && code <= 0x309f) || // Hiragana
        (code >= 0x30a0 && code <= 0x30ff) || // Katakana
        (code >= 0x4e00 && code <= 0x9fff) || // CJK Ideographs
        (code >= 0xff00 && code <= 0xffef) || // Full-width ASCII
        (code >= 0x3000 && code <= 0x303f) // Full-width symbols
      ) {
        width += 2; // Full-width characters count as 2
      } else {
        width += 1; // Half-width characters count as 1
      }
    }
    return width;
  };

  // IME変換中の状態を追跡するためのステート
  const [isComposing, setIsComposing] = useState(false);

  // IME変換の開始処理
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  // Convert YYYY/MM/DD or YYYY-MM-DD string to Date object
  const stringToDate = (dateString) => {
    if (!dateString) return null;
    // Handle YYYY-MM-DD format (from date input)
    if (dateString.includes("-")) {
      const parts = dateString.split("-");
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        const date = new Date(year, month - 1, day);
        return Number.isNaN(date.getTime()) ? null : date;
      }
    }
    // Handle YYYY/MM/DD format
    const parts = dateString.split("/");
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  // Convert Date object to YYYY/MM/DD string
  const dateToString = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  // IME変換の終了処理
  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    // Force validation after composition
    validateAndUpdate(e);
  };

  // 検証および更新を行う関数
  const validateAndUpdate = (e) => {
    const { name, value } = e.target;

    // 各フィールドの最大文字数定義
    const fieldMaxWidths = {
      product_no: 10, // 製番 - 全角5文字
      assignee: 20, // 担当者 - 全角10文字
      destination: 40, // 仕向先 - 全角20文字
      customer: 40, // 得意先 - 全角20文字
      device_name: 40, // 設備名 - 全角20文字
      remarks: 40, // 備考 - 全角20文字
      assembly_type: 40, // 組立種類 - 全角20文字
    };

    const maxWidth = fieldMaxWidths[name];
    if (maxWidth) {
      const currentWidth = getCharacterWidth(value);
      if (currentWidth > maxWidth) {
        // 最大文字数に合わせて切り詰める
        let truncatedValue = "";
        let width = 0;
        for (let i = 0; i < value.length; i++) {
          const char = value[i];
          const code = char.charCodeAt(0);
          const charWidth =
            (code >= 0x3040 && code <= 0x309f) || // Hiragana
            (code >= 0x30a0 && code <= 0x30ff) || // Katakana
            (code >= 0x4e00 && code <= 0x9fff) || // CJK Ideographs
            (code >= 0xff00 && code <= 0xffef) || // Full-width ASCII
            (code >= 0x3000 && code <= 0x303f) // Full-width symbols
              ? 2
              : 1;

          if (width + charWidth > maxWidth) break;
          truncatedValue += char;
          width += charWidth;
        }

        if (onFormDataChange) {
          onFormDataChange(name, truncatedValue);
        }
        return;
      }
    }

    // 通常の更新
    if (onFormDataChange) {
      onFormDataChange(name, value);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // IME構成中は検証スキップ（ただし値は更新）
    if (isComposing) {
      // IME中でも値は更新しておく（表示のために）
      if (onFormDataChange) {
        onFormDataChange(name, value);
      }
      return;
    }

    // IME構成以外の変更時に検証
    validateAndUpdate(e);
  };

  const handleDateChange = (date) => {
    const dateString = date ? dateToString(date) : "";
    if (onFormDataChange) {
      onFormDataChange("issue_date", dateString);
    }
  };

  // Options cho dropdown
  const drawingTypeOptions = (documentCategories || []).map((item) => ({
    value: String(item.document_category),
    label: item.document_category_text,
  }));

  // Highlight weekends
  const highlightWeekends = (date) => {
    const day = date.getDay();
    if (day === 0) {
      return "highlight-sunday";
    } else if (day === 6) {
      return "highlight-saturday";
    }
    return "";
  };

  // Get date value for DatePicker
  const getDateValue = () => {
    if (formData?.issue_date) {
      return stringToDate(formData.issue_date);
    }
    return null;
  };

  return (
    <div className="pattern-form">
      <div className="form-grid pattern-system-grid">
        {/* 1番目行: 製番 + 図面種類（システム装置） */}
        <div className="form-row">
          <label className="form-label">製番</label>
          <input
            type="text"
            name="product_no"
            value={formData?.product_no || ""}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label className="form-label">図面種類（システム装置）</label>
          <select
            name="document_category"
            value={formData?.document_category || ""}
            onChange={handleChange}
            className="form-select"
          >
            <option value=""></option>
            {drawingTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 2番目行: 仕向先 + 日付 */}
        <div className="form-row">
          <label className="form-label">仕向先</label>
          <input
            type="text"
            name="destination"
            value={formData?.destination || ""}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label className="form-label">日付</label>
          <div className="mod-form__text calendar">
            <DatePicker
              selected={getDateValue()}
              onChange={handleDateChange}
              dayClassName={highlightWeekends}
              locale="ja"
              dateFormat="yyyy年MM月dd日"
              className="custom-datepicker"
              calendarStartDay={1}
              placeholderText="日付を選択"
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
              }) => {
                const currentYear = new Date().getFullYear();
                const years = Array.from(
                  { length: 30 },
                  (_, i) => currentYear - i
                );

                const months = [
                  "01",
                  "02",
                  "03",
                  "04",
                  "05",
                  "06",
                  "07",
                  "08",
                  "09",
                  "10",
                  "11",
                  "12",
                ];

                return (
                  <div
                    className="custom-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                    }}
                  >
                    {/* Prev button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        decreaseMonth();
                      }}
                    >
                      {"<"}
                    </button>

                    {/* Year + Month display + dropdown */}
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <select
                          className="year-select"
                          value={date.getFullYear()}
                          onChange={(e) => changeYear(e.target.value)}
                          style={{ padding: "2px 4px" }}
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                        <span>年</span>
                      </div>

                      <div>
                        <select
                          value={date.getMonth()}
                          onChange={(e) => changeMonth(e.target.value)}
                          style={{ padding: "2px 4px" }}
                          className="month-select"
                        >
                          {months.map((m, i) => (
                            <option key={i} value={i}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <span>月</span>
                      </div>
                    </div>

                    {/* Next button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        increaseMonth();
                      }}
                    >
                      {">"}
                    </button>
                  </div>
                );
              }}
              onKeyDown={(e) => e.preventDefault()}
            />
          </div>
        </div>

        {/* 3番目行: 得意先 + 担当者 */}
        <div className="form-row">
          <label className="form-label">得意先</label>
          <input
            type="text"
            name="customer"
            value={formData?.customer || ""}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label className="form-label">担当者</label>
          <input
            type="text"
            name="assignee"
            value={formData?.assignee || ""}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
          />
        </div>

        {/* 4番目行: 設備名 + 備考 */}
        <div className="form-row">
          <label className="form-label">設備名</label>
          <input
            type="text"
            name="device_name"
            value={formData?.device_name || ""}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label className="form-label">備考</label>
          <input
            type="text"
            name="remarks"
            value={formData?.remarks || ""}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
          />
        </div>

        {/* 5番目行: 組立種類 */}
        <div className="form-row">
          <label className="form-label">組立種類</label>
          <input
            type="text"
            name="assembly_type"
            value={formData?.assembly_type || ""}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
          />
        </div>

        <div className="form-row form-row-placeholder" aria-hidden="true">
          <label className="form-label">&nbsp;</label>
        </div>
      </div>
    </div>
  );
};

export default PatternSystemEdit;
